import React, { useState } from 'react';
import {
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { ChargerHistoryAttributes } from '../models/charger-history-model';
//@ts-ignore
import DailyRevenueLineChart from '../components/graphs/DailyRevenueLineChart';
import { HeaderWithMenu } from '../components/menu';

const { width, height } = Dimensions.get('window');

export default function MyChargerHistoryAtAGlance(props: {
  handleFilter: () => void;
  customDates: { start: string | null; end: string | null };
  filteredChargers: ChargerHistoryAttributes[];
  totalKwh: number;
  totalRevenue: number;
  dailyRevenueData?: Array<{ date: string; revenue: number }>;
  onInsightsPress?: () => void;
  isLoading?: boolean;
}) {
  const [isPressed, setIsPressed] = useState(false);

  // Format kWh with comma separation and switch to MWh for values >= 10,000
  const useMWh = props.totalKwh >= 10000;
  const energyDisplay = useMWh
    ? props.totalKwh / 1000
    : props.totalKwh;
  const energyUnit = useMWh ? 'MWh' : 'kWh';
  const formattedEnergy = new Intl.NumberFormat(undefined, { 
    minimumFractionDigits: 1,
    maximumFractionDigits: 1 
  }).format(energyDisplay);

  return (
    <React.Fragment>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {/* Hamburger Menu - Fixed on left */}
        <HeaderWithMenu forceHamburger transparent insideSafeArea />
        
        {/* Centered Content - Calendar + Title */}
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, marginRight: 44 }}>
          {/* Calendar Filter Button */}
          <Pressable
            onPressIn={() => setIsPressed(true)}
            onPressOut={() => setIsPressed(false)}
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
              props.handleFilter();
            }}
            style={{
              height: 40,
              width: 40,
              borderRadius: 25,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: isPressed ? '#FFB84D' : '#39BEB7',
            }}
          >
            <View style={{ marginTop: 1 }}>
              <FontAwesome name="calendar" size={20} color="white" />
            </View>
          </Pressable>
          
          {/* Title and Date Range */}
          <View>
            <Text className="text-lg font-semibold text-white">Charger Dashboard</Text>
            <Text
              className="text-xs font-normal"
              style={styles.TextColor1}
            >{`${props.customDates.start} - ${props.customDates.end}`}</Text>
          </View>
        </View>
      </View>

      {/* Consolidated Top Line Metrics */}
      <View style={{ marginTop: 12, backgroundColor: '#292D33', borderRadius: 14, paddingVertical: 12, paddingHorizontal: 8 }}>
        {props.isLoading ? (
          <View style={{ paddingVertical: 12, alignItems: 'center' }}>
            <ActivityIndicator size="small" color="#39BEB7" />
            <Text style={{ color: '#8D8E90', marginTop: 8, fontSize: 12 }}>
              Loading metrics...
            </Text>
          </View>
        ) : (
          <View className="flex flex-row justify-around items-center">
            <View className="flex-1 items-center">
              <View className="flex flex-row gap-2 items-center">
                <Image
                  source={require('../../assets/stationPlugInWhite.png')}
                  style={{
                    height: 24,
                    width: 18,
                    tintColor: '#39BEB7',
                  }}
                />
                <View className="items-center">
                  <Text className="text-xl font-semibold text-white">
                    {props.filteredChargers?.length}
                  </Text>
                  <Text className="text-xs font-medium text-[#8D8E90]">
                    Charges
                  </Text>
                </View>
              </View>
            </View>
            <View style={{ width: 1, height: 40, backgroundColor: '#636363' }} />
            <View className="flex-1 items-center">
              <View className="flex flex-row gap-2 items-center">
                <Image
                  source={require('../../assets/lightning.png')}
                  style={{
                    height: 24,
                    width: 14,
                    tintColor: '#FFB84D',
                  }}
                />
                <View className="items-center">
                  <Text className="text-xl font-semibold text-white">
                    {formattedEnergy}
                  </Text>
                  <Text className="text-xs font-medium text-[#8D8E90]">
                    {energyUnit}
                  </Text>
                </View>
              </View>
            </View>
            <View style={{ width: 1, height: 40, backgroundColor: '#636363' }} />
            <View className="flex-1 items-center">
              <View className="flex flex-row gap-2 items-center">
                <FontAwesome name="dollar" size={20} color="#51B868" />
                <View className="items-center">
                  <Text className="text-xl font-semibold text-white">
                    {props.totalRevenue.toFixed(2)}
                  </Text>
                  <Text className="text-xs font-medium text-[#8D8E90]">
                    Revenue
                  </Text>
                </View>
              </View>
            </View>
          </View>
        )}
      </View>

      {/* Daily Revenue Chart */}
      {!props.isLoading && props.dailyRevenueData && props.dailyRevenueData.length > 0 && (
        <View style={{ marginTop: 12, backgroundColor: '#292D33', borderRadius: 14, paddingVertical: 10, paddingHorizontal: 12 }}>
          <Text className="text-sm font-semibold text-[#FFB84D] mb-1">Daily Revenue</Text>
          <DailyRevenueLineChart 
            data={props.dailyRevenueData} 
            width={'100%'} 
            height={100}
            lineColor="#FFB84D"
          />
        </View>
      )}

      {/* Insights Link */}
      {!props.isLoading && props.onInsightsPress && (
        <Pressable
          onPress={props.onInsightsPress}
          style={({ pressed }) => [
            styles.insightsButton,
            { opacity: pressed ? 0.7 : 1, marginTop: 12 }
          ]}
        >
          <View className="flex flex-row justify-between items-center">
            <View className="flex flex-row gap-2 items-center">
              <Image
                source={require('../../assets/lightning.png')}
                style={{
                  height: 20,
                  width: 12,
                  tintColor: '#FFB84D',
                }}
              />
              <Text className="text-sm font-semibold text-white">
                View Detailed Insights
              </Text>
            </View>
            <FontAwesome name="chevron-right" size={14} color="#FFB84D" />
          </View>
        </Pressable>
      )}
    </React.Fragment>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  BgColor: {
    backgroundColor: '#51B868',
  },
  BgColor1: {
    backgroundColor: '#39BEB780',
  },
  BgColor2: {
    backgroundColor: '#F97777',
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  BgColor4: {
    backgroundColor: '#636363',
  },
  TextColor1: {
    color: '#E8E8E9',
  },
  textColor3: {
    color: '#8D8E90',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  filterIcon: {
    height: 40, // Increased to give the container proper space
    width: 40, // Match the height to maintain the circle
    backgroundColor: '#39BEB7',
    borderRadius: 25, // Half of width/height for a perfect circle
    justifyContent: 'center', // Center content vertically
    alignItems: 'center', // Center content horizontally
  },
  imageStyle: {
    width: '60%', // Adjust size relative to container
    height: '60%', // Maintain aspect ratio
    resizeMode: 'contain', // Scale the image proportionally
  },
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 22,
  },
  modalView: {
    margin: 20,
    backgroundColor: '#292D33',
    borderRadius: 30,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: 290,
    height: 250,
  },
  dropdownTrigger: {
    width: width <= 430 ? width * 0.4 : 150,
  },
  dropdownContainer: {
    position: 'absolute',
    top: 30,
    left: 0,
    backgroundColor: '#fff',
    borderRadius: 5,
    marginTop: 2,
    width: width <= 430 ? width * 0.4 : 150,
    zIndex: 1000,
  },
  alignEllipses: Platform.select({
    web: {
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 150,
    },
    default: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
  }),
  tooltipContainer: {
    borderRadius: 10,
    padding: 10,
    minWidth: 50,
    maxWidth: 500,
    width: 'auto',
    height: 'auto',
  },
  insightsButton: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#FFB84D',
  },
});
